import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTicketEvents, type TicketEventMessage } from '../lib/events';
import { playNewTicketChime } from '../lib/sound';
import { TicketNotificationCoalescer } from '../lib/notification-coalescer';
import {
  isInAudience,
  renderNotification,
  shouldSuppressNotification,
} from '../lib/notification-text';
import type { UserSummary } from '../lib/api';

const DURATION_MS = 5000;
// Window for collapsing a burst of ticket.updated / ticket.comment_added events on
// a single ticket into one consolidated toast. Discrete events (new ticket, urgent
// escalation, deletion) still fire immediately.
const COALESCE_WINDOW_MS = 60_000;

interface Toast {
  id: string;
  /** Path to navigate to when the toast is clicked. May include query + anchor. */
  path: string;
  title: string;
  body: string;
}

/**
 * In-app toast queue. Mounted once in Layout — fires on every SSE event,
 * regardless of current screen. Filters out self-actions, applies the audience
 * filter (involved + mentioned only, broadcast events excepted), and drops
 * bookkeeping-only events. Comment toasts deep-link to the activity tab.
 */
export function ToastQueue({ me }: { me: UserSummary | null }) {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const meRef = useRef<UserSummary | null>(me);
  meRef.current = me;
  const selfUserIdRef = useRef<string | null>(me?.id ?? null);
  selfUserIdRef.current = me?.id ?? null;

  const pushToast = useRef((t: Toast) => {
    setToasts((prev) => [...prev, t]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== t.id));
    }, DURATION_MS);
  }).current;

  const coalescer = useRef<TicketNotificationCoalescer | null>(null);
  if (coalescer.current === null) {
    coalescer.current = new TicketNotificationCoalescer(
      COALESCE_WINDOW_MS,
      (n) => {
        pushToast({
          id: crypto.randomUUID(),
          path: n.path,
          title: n.title,
          body: n.body,
        });
      },
      selfUserIdRef,
    );
  }
  useEffect(() => {
    return () => {
      coalescer.current?.dispose();
      coalescer.current = null;
    };
  }, []);

  useTicketEvents((msg) => {
    if (!shouldShowToast(msg, meRef.current?.id ?? null)) return;

    const myId = meRef.current?.id ?? null;
    const payload = msg.payload ?? {};
    const isAssignedToMe = !!myId && payload.toAssigneeUserId === myId;
    const isMentioned =
      !!myId &&
      Array.isArray(payload.mentionedUserIds) &&
      (payload.mentionedUserIds as string[]).includes(myId);
    if (msg.type === 'ticket.created') {
      playNewTicketChime();
    } else if (msg.type === 'ticket.comment_added' && (isAssignedToMe || isMentioned)) {
      playNewTicketChime();
    }

    if (coalescer.current?.ingest(msg)) return;
    const rendered = renderNotification(msg, myId);
    pushToast({
      id: crypto.randomUUID(),
      path: rendered.path,
      title: rendered.title,
      body: rendered.body,
    });
  });

  function dismiss(id: string) {
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }
  function onClick(t: Toast) {
    navigate(t.path);
    dismiss(t.id);
  }

  // Auto-expire safety net — if something throws between push and setTimeout firing,
  // we still don't pile up forever.
  useEffect(() => {
    if (toasts.length === 0) return;
    const oldest = toasts[0]!;
    const ageMs = Date.now() - parseInt(oldest.id.slice(0, 8), 16);
    if (ageMs > DURATION_MS * 2) {
      setToasts((prev) => prev.slice(1));
    }
  }, [toasts]);

  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-[calc(100vw-2rem)] sm:w-80 pointer-events-none"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="pointer-events-auto bg-slate-900 text-white rounded-lg shadow-xl overflow-hidden animate-slide-in-right"
        >
          <button
            onClick={() => onClick(t)}
            className="block w-full text-left px-3 py-2.5 hover:bg-slate-800 transition-colors"
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{t.title}</div>
                {t.body && (
                  <div className="text-xs text-slate-300 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                    {t.body}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  dismiss(t.id);
                }}
                className="text-slate-500 hover:text-white -mt-0.5 -mr-1 p-0.5"
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </button>
        </div>
      ))}
    </div>
  );
}

function shouldShowToast(msg: TicketEventMessage, selfUserId: string | null): boolean {
  if (selfUserId && msg.actorUserId === selfUserId) return false;
  if (!isInAudience(msg, selfUserId)) return false;
  if (shouldSuppressNotification(msg)) return false;
  return true;
}
